import AppContext from "@/AppContext";
import Layout from "@/components/elements/Layout";
import OpinionCard from "@/components/items/OpinionCard";
import LoadingContent from "@/components/loader/LoadingContent";
import Schema from "@/components/schema/Schema";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useGetContentList } from "@/hooks/content";
import { getContentsByContentTypeName } from "@/services/api";
import { CONTENT_TYPES } from "@/utils/content";
import {
  MagnifyingGlass,
  ArrowsDownUp,
  CaretDown,
  X,
} from "@phosphor-icons/react";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useRouter } from "next/router";
import { getSEOByPathname } from "@/services/api/SEO";
import SortMobile from "@/components/drawer/sort-mobile";

const spreadOpinionsByAuthor = (opinions) => {
  // Step 1: Group opinions by author
  const opinionsByAuthor = {};
  opinions.forEach((opinion) => {
    const authorId = opinion.author.id;
    if (!opinionsByAuthor[authorId]) {
      opinionsByAuthor[authorId] = [];
    }
    opinionsByAuthor[authorId].push(opinion);
  });

  // Step 2: Create a list to spread opinions
  const spreadOpinions = [];
  let roundRobinArray = Object.values(opinionsByAuthor);
  while (roundRobinArray.length > 0) {
    roundRobinArray = roundRobinArray.filter((opinions) => opinions.length > 0);
    roundRobinArray.forEach((opinions) => {
      if (opinions.length > 0) {
        spreadOpinions.push(opinions.shift());
      }
    });
  }

  return spreadOpinions;
};

export default function Opinions({ opinions: _opinions, seoData }) {
  const appContext = useContext(AppContext);
  const opinions = spreadOpinionsByAuthor(_opinions);
  const onFetchError = function (error) {
    console.log("Error", error);
    toast.error("An error occurred when fetching opinion data!");
  };
  const {
    contents,
    isLoading,
    currentPage,
    fetchContents,
    search,
    setSearch,
    topic,
    setTopic,
    total,
    lastPage,
    sortInput,
    setSortInput,
    tags,
    setTags,
  } = useGetContentList({
    type: CONTENT_TYPES.OPINIONS,
    onErrorCallback: onFetchError,
    // initialData: opinions,
    is_random: true,
  });

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [openTopicFilter, setOpenTopicFilter] = useState(false);
  const [searchTopicQuery, setSearchTopicQuery] = useState("");
  const [tempSelectedTopics, setTempSelectedTopics] = useState([]);
  const router = useRouter();
  const isMobile = useIsMobile();

  const handleScroll = () => {
    const isBottom =
      window.innerHeight + window.scrollY >= document.body.offsetHeight - 1;
    if (isBottom && currentPage < lastPage && !isLoading) {
      fetchContents(currentPage + 1);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [currentPage, lastPage, isLoading]);

  const structuredData = [
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "News",
          item: `${process.env.BASE_URL}news`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: opinions.map((item, index) => {
        return {
          "@type": "ListItem",
          position: index + 1,
          url: `${process.env.BASE_URL}opinions/${item.slug}`,
          name: item.title,
        };
      }),
    },
  ];

  const onChange = (e) => {
    setSortInput(e);
  };

  const filteredTopics = appContext.state.topic_list.filter((topic) =>
    topic.name.toLowerCase().includes(searchTopicQuery.toLowerCase())
  );

  const handleTopicChange = (event, topic) => {
    const isChecked = event.target.checked;
    setTempSelectedTopics((prev) =>
      isChecked ? [...prev, topic] : prev.filter((t) => t.uuid !== topic.uuid)
    );
  };

  const applyTopicFilter = () => {
    setSelectedTopics(tempSelectedTopics);
    setTags(tempSelectedTopics.map((topic) => topic.name).join(";"));
    setOpenTopicFilter(false);
  };

  const removeTopicFilter = () => {
    setTempSelectedTopics([]);
    setSelectedTopics([]);
    setTags("");
  };

  useEffect(() => {
    setTempSelectedTopics(selectedTopics);
  }, [openTopicFilter]);

  useEffect(() => {
    const { q } = router.query;
    if (q) {
      setSearch(q);
    }
  }, [router.query]);

  const handleSearchInput = (evt) => {
    const searchValue = evt.target.value;
    if (searchValue === "" && sortInput === "") {
      setSortInput("newest");
    }
    setSearch(searchValue);

    // Remove 'q' parameter from URL when search is cleared, only if it exists
    if (searchValue === "" && router.query.q) {
      const { q, ...query } = router.query;
      router.push(
        {
          pathname: router.pathname,
          query: query,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        seoData={seoData}
        title='Opinions'
        description={
          "Read insightful articles & opinions from venture capital firms members and industry experts. Stay informed in the latest VC news & startups"
        }
      >
        <div className='container py-8'>
          <h1 className='text-4xl font-semibold leading-tight text-white lg:font-bold lg:text-6xl font-display'>
            Opinions
          </h1>
          <p className='mt-3 text-base leading-relaxed lg:mt-5 lg:text-lg text-white/50'>
            {seoData.page_description ||
              `Opinions collects first-person commentary from the people building the MENAPT technology economy — founders, venture capitalists, operators, and policymakers writing under their own names. Each piece argues a position on where capital, talent, or regulation should go next, and why the region's ecosystems do not follow the Silicon Valley playbook. Views belong to the authors, not to Lucidity Insights. Browse the latest below, or filter by topic to follow a debate in a specific sector.`}
          </p>

          <div className='flex flex-wrap items-center mt-5 gap-x-2 border-white/10 gap-y-3 lg:mt-8'>
            <div className='relative w-full'>
              <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none opacity-40'>
                <MagnifyingGlass className='text-lg' />
              </div>
              <Input
                type='text'
                placeholder='Search...'
                value={search}
                onInput={handleSearchInput}
                className='w-full transition-colors text-sm lg:text-base placeholder:text-white/40 !h-10 lg:!h-12 text-white rounded-lg bg-white/10 pl-10 focus:bg-white/15'
              />
            </div>
            <p className='flex-1 text-xs lg:text-base'>
              <span className='opacity-30'>Showing</span>{" "}
              {contents.length > 0 ? `1 - ${contents.length}` : 0}{" "}
              <span className='opacity-30'>of</span> {total}
            </p>
            {!isMobile ? (
              <Select value={sortInput} onValueChange={onChange}>
                <SelectTrigger
                  hideDropdownIcon
                  className='w-auto text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  <div className='flex items-center space-x-2'>
                    <ArrowsDownUp className='text-base lg:text-lg' />
                    <SelectValue placeholder={"Most Recent"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"newest"}>Most Recent</SelectItem>
                  <SelectItem value={"most_viewed"}>Most Viewed</SelectItem>
                  {search && <SelectItem value={""}>Most Relevant</SelectItem>}
                </SelectContent>
              </Select>
            ) : (
              <SortMobile
                sortOption={sortInput}
                onChange={onChange}
                options={[
                  { value: "newest", label: "Most Recent" },
                  { value: "most_viewed", label: "Most Viewed" },
                  { value: "", label: "Most Relevant" },
                ]}
              />
            )}
            <Popover open={openTopicFilter} onOpenChange={setOpenTopicFilter}>
              <PopoverTrigger asChild>
                <Button
                  onClick={() => setOpenTopicFilter(true)}
                  className='text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  Topics
                  {selectedTopics.length > 0 && (
                    <span className='ml-1 inline-flex items-center justify-center w-4 h-4 font-semibold bg-white text-[10px] tracking-tighter leading-none rounded-full text-background'>
                      <span>{selectedTopics.length}</span>
                    </span>
                  )}
                  <CaretDown className='size-4 ml-2.5 -mr-1' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-64 bg-black h-80'>
                <div>
                  <Input
                    type='text'
                    placeholder='Search topics...'
                    value={searchTopicQuery}
                    onChange={(e) => setSearchTopicQuery(e.target.value)}
                    className='mb-2'
                  />
                </div>
                <ScrollArea className='w-56 h-56'>
                  {filteredTopics.map((topic, index) => (
                    <div key={index} className='flex items-center p-2 text-xs'>
                      <input
                        type='checkbox'
                        checked={
                          !!tempSelectedTopics.find(
                            (t) => t.uuid === topic.uuid
                          )
                        }
                        onChange={(event) => handleTopicChange(event, topic)}
                        id={`topic-${topic.uuid}`}
                        className='mr-2'
                      />
                      <label htmlFor={`topic-${topic.uuid}`}>
                        {topic.name}
                      </label>
                    </div>
                  ))}
                </ScrollArea>
                <div className='border-b border-r border-l rounded-b mt-2 pt-2 flex items-center justify-between p-2 bg-black -ml-4 w-[114.8%]'>
                  <Button
                    onClick={removeTopicFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={selectedTopics.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyTopicFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {selectedTopics && selectedTopics.length > 0 && (
              <div className='flex flex-wrap order-5 w-full gap-2'>
                {selectedTopics.map((topic) => (
                  <Badge
                    key={`selected-topic-${topic.uuid}`}
                    variant='secondary'
                    className='text-white bg-white/10 hover:bg-white/20'
                  >
                    {topic.name}
                    <button
                      className='ml-1 hover:text-red-500'
                      onClick={() => {
                        setSelectedTopics((prev) =>
                          prev.filter((t) => t.uuid !== topic.uuid)
                        );
                        setTags((prev) =>
                          prev
                            .split(";")
                            .filter((t) => t !== topic.name)
                            .join(";")
                        );
                      }}
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* <div className='flex mt-8 space-x-3'>
            <div className='relative flex-1'>
              <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                <MagnifyingGlass size={14} color='#fff' weight='bold' />
              </div>
              <Input
                type='text'
                placeholder='Search...'
                value={search}
                onInput={handleSearchInput}
                className='w-full pl-9'
              />
            </div>
            <Popover
              open={openTopicFilter}
              onOpenChange={setOpenTopicFilter}
            >
              <PopoverTrigger asChild>
                <Button
                  onClick={() => setOpenTopicFilter(true)}
                  className='h-auto px-2 py-1.5 text-xs text-white border bg-white/5 hover:bg-white/10 border-white/5'
                >
                  Topics
                  {selectedTopics.length > 0 && (
                    <span className='ml-1 inline-flex items-center justify-center w-4 h-4 font-semibold bg-white text-[10px] tracking-tighter leading-none rounded-full text-background'>
                      <span>{selectedTopics.length}</span>
                    </span>
                  )}
                  <CaretDown className='w-3.5 h-3.5 ml-1.5 -mr-1' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-64 bg-black h-80'>
                <div className='p-2'>
                  <Input
                    type='text'
                    placeholder='Search topics...'
                    value={searchTopicQuery}
                    onChange={(e) => setSearchTopicQuery(e.target.value)}
                    className='mb-2'
                  />
                </div>
                <ScrollArea className='w-56 h-56'>
                  {filteredTopics.map((topic, index) => (
                    <div
                      key={index}
                      className='flex items-center p-2 text-xs'
                    >
                      <input
                        type='checkbox'
                        checked={!!tempSelectedTopics.find((t) => t.uuid === topic.uuid)}
                        onChange={(event) => handleTopicChange(event, topic)}
                        id={`topic-${topic.uuid}`}
                        className='mr-2'
                      />
                      <label htmlFor={`topic-${topic.uuid}`}>
                        {topic.name}
                      </label>
                    </div>
                  ))}
                </ScrollArea>
                <div className='border-b border-r border-l rounded-b mt-2 pt-2 flex items-center justify-between p-2 bg-black -ml-4 w-[114.8%]'>
                  <Button
                    onClick={removeTopicFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={selectedTopics.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyTopicFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Select value={sortInput} onValueChange={onChange}>
              <SelectTrigger className='text-white border w-44 bg-white/5 hover:bg-white/10 border-white/5'>
                <div className='flex items-center space-x-2'>
                  <ArrowsDownUp className='text-lg' />
                  <SelectValue placeholder={"Most Recent"} />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={"newest"}>Most Recent</SelectItem>
                <SelectItem value={"most_viewed"}>Most Viewed</SelectItem>
                {search && <SelectItem value={""}>Most Relevant</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-wrap gap-2 mt-4'>
            {selectedTopics.map((topic) => (
              <Badge
                key={`selected-topic-${topic.uuid}`}
                variant="secondary"
                className="text-white bg-white/10 hover:bg-white/20"
              >
                {topic.name}
                <button
                  className="ml-1 hover:text-red-500"
                  onClick={() => {
                    setSelectedTopics(prev => prev.filter(t => t.uuid !== topic.uuid));
                    setTags(prev => prev.split(';').filter(t => t !== topic.name).join(';'));
                  }}
                >
                  <X size={14} />
                </button>
              </Badge>
            ))}
          </div> */}

          {contents.length > 0 && (
            <h2 className='pt-8 text-xl font-semibold tracking-tight lg:pt-10 lg:text-2xl'>
              Latest MENAPT Startup Opinions
            </h2>
          )}
          {contents.length > 0 ? (
            <div className='grid grid-cols-2 gap-2 pt-8 lg:gap-4 lg:grid-cols-5'>
              {contents.map((item, index) => (
                <OpinionCard
                  data={item}
                  key={`opinion-${item.uuid}-${index}`}
                />
              ))}
            </div>
          ) : !isLoading ? (
            <div className='flex flex-col items-center justify-center py-16'>
              <MagnifyingGlass
                size={48}
                className='mb-4 text-white opacity-50'
              />
              <p className='text-lg text-white text-opacity-80'>
                No opinions found
              </p>
              <p className='mt-2 text-white text-opacity-60'>
                Try adjusting your search or filters
              </p>
            </div>
          ) : null}

          {isLoading && (
            <div className='flex justify-center mt-8'>
              <LoadingContent />
            </div>
          )}
        </div>
      </Layout>
    </div>
  );
}

export async function getServerSideProps() {
  let opinions = [];
  try {
    const opinionsData = await getContentsByContentTypeName(
      CONTENT_TYPES.OPINIONS
    );
    opinions = opinionsData?.data?.data || [];
  } catch (error) {
    console.error("Error fetching opinions for structured data:", error);
  }

  let seoData = {};
  try {
    const seoResponse = await getSEOByPathname("opinions");
    seoData = seoResponse.data;
  } catch (error) {
    console.error("Error fetching SEO data:", error);
  }

  return {
    props: {
      opinions,
      seoData,
    },
  };
}
